// src/components/ProgressManager.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, collection, getDocs, doc, getDoc, onSnapshot } from '../firebase/firebase';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Loader2, User, BookOpen, ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../lib/utils';
// Import Editor for code display
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-java';
import 'prismjs/themes/prism-dark.css'; // Or any other PrismJS theme you prefer

const ProgressManager = ({ allProblems, isTeacher, teacherSections }) => {
    const [studentsSummary, setStudentsSummary] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedStudentProblems, setSelectedStudentProblems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSectionFilter, setSelectedSectionFilter] = useState('all');
    const [selectedProblemFilter, setSelectedProblemFilter] = useState('all');

    // NEW STATES for code review modal
    const [showCodeReviewModal, setShowCodeReviewModal] = useState(false);
    const [codeToReview, setCodeToReview] = useState('');
    const [problemTitleForReview, setProblemTitleForReview] = useState('');

    const unsubscribeRef = useRef(null);

    const fetchStudentsSummary = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const usersCollectionRef = collection(db, 'users');
            const usersSnapshot = await getDocs(usersCollectionRef);
            
            const studentsData = [];

            for (const userDoc of usersSnapshot.docs) {
                const userData = userDoc.data();
                if (userData.role !== 'teacher') {
                    const studentId = userDoc.id;

                    const problemsSolvedRef = collection(db, `users/${studentId}/problems_solved`);
                    const problemsSolvedSnapshot = await getDocs(problemsSolvedRef);
                    
                    let passedTestsOverall = 0;
                    let totalTestsOverall = 0;
                    let problemsCompletedCount = 0;

                    const studentProblemsProgress = {};

                    problemsSolvedSnapshot.forEach(problemDoc => {
                        const problemProgress = problemDoc.data();
                        studentProblemsProgress[problemDoc.id] = problemProgress;
                        passedTestsOverall += (problemProgress.passedTests || 0);
                        totalTestsOverall += (problemProgress.totalTests || 0);
                        if (problemProgress.completed) {
                            problemsCompletedCount++;
                        }
                    });

                    const assignedSections = teacherSections.filter(section => {
                        const sectionStudentIds = section.studentIds;
                        const isStudentAssigned = sectionStudentIds && Array.isArray(sectionStudentIds) && sectionStudentIds.includes(studentId);
                        return isStudentAssigned;
                    }).map(section => ({ id: section.id, name: section.name }));

                    const totalAvailableProblems = allProblems.length;

                    studentsData.push({
                        uid: studentId,
                        email: userData.email || 'N/A',
                        name: userData.name || userData.email.split('@')[0],
                        problemsCompletedCount: problemsCompletedCount,
                        totalAvailableProblems: totalAvailableProblems,
                        passedTestsOverall: passedTestsOverall,
                        totalTestsOverall: totalTestsOverall,
                        detailedProgress: studentProblemsProgress,
                        assignedSections: assignedSections
                    });
                } else {
                }
            }
            setStudentsSummary(studentsData);
        } catch (err) {
            console.error("ProgressManager: Error fetching students summary:", err);
            setError(`Failed to load student summaries: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [allProblems, teacherSections]);

    const fetchSelectedStudentDetails = useCallback(async (studentUid) => {
        setLoading(true);
        setError(null);

        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }

        try {
            const userDocRef = doc(db, 'users', studentUid);
            const userDocSnap = await getDoc(userDocRef);
            if (!userDocSnap.exists()) {
                throw new Error("Student not found.");
            }
            setSelectedStudent({ uid: studentUid, ...userDocSnap.data() });

            const problemsSolvedRef = collection(db, `users/${studentUid}/problems_solved`);
            
            unsubscribeRef.current = onSnapshot(problemsSolvedRef, (snapshot) => {
                const studentProblemsData = {};
                snapshot.forEach(doc => {
                    studentProblemsData[doc.id] = doc.data();
                });

                const combinedProblems = allProblems.map(problem => ({
                    ...problem,
                    // Fix: Only set savedCode if it exists in Firestore data
                    progress: { 
                        completed: studentProblemsData[problem.id]?.completed || false,
                        passedTests: studentProblemsData[problem.id]?.passedTests || 0,
                        totalTests: studentProblemsData[problem.id]?.totalTests || problem.tests?.length || 0,
                        savedCode: studentProblemsData[problem.id]?.savedCode || null // Default to null if no saved code
                    } 
                }));
                setSelectedStudentProblems(combinedProblems);
                setLoading(false);
            }, (err) => {
                console.error("ProgressManager: Error listening to student problems:", err);
                setError(`Failed to load selected student's progress: ${err.message}`);
                setLoading(false);
                unsubscribeRef.current = null;
            });

        } catch (err) {
            console.error("ProgressManager: Error fetching selected student details (outer catch):", err);
            setError(`Failed to load student details: ${err.message}`);
            setLoading(false);
            unsubscribeRef.current = null;
        }
    }, [allProblems]);

    useEffect(() => {
        if (isTeacher) {
            fetchStudentsSummary();
        } else {
            setStudentsSummary([]);
            setSelectedStudent(null);
            setSelectedStudentProblems([]);
            setLoading(false);
            setError("You must be a teacher to view student progress.");
        }
    }, [isTeacher, fetchStudentsSummary]);

    useEffect(() => {
        if (selectedStudent) {
            // No need to call fetchSelectedStudentDetails here explicitly,
            // as it's handled by the handleViewDetails function.
        }

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [selectedStudent]);

    const handleViewDetails = (studentUid) => {
        const student = studentsSummary.find(s => s.uid === studentUid);
        if (student) {
            setSelectedStudent(student);
            fetchSelectedStudentDetails(student.uid);
        } else {
            console.warn(`ProgressManager: Student with UID ${studentUid} not found in summary.`);
        }
    };

    const handleBackToSummary = () => {
        setSelectedStudent(null);
        setSelectedStudentProblems([]);
        setShowCodeReviewModal(false); // Ensure modal is closed
        setCodeToReview(''); // Clear code
        setProblemTitleForReview(''); // Clear title
        fetchStudentsSummary();
    };

    // NEW: Function to open code review modal
    const handleViewCode = (problemTitle, savedCode) => {
        setProblemTitleForReview(problemTitle);
        setCodeToReview(savedCode);
        setShowCodeReviewModal(true);
    };

    // NEW: Function to close code review modal
    const handleCloseCodeReviewModal = () => {
        setShowCodeReviewModal(false);
        setCodeToReview('');
        setProblemTitleForReview('');
    };


    const filteredStudents = selectedSectionFilter === 'all'
        ? studentsSummary
        : studentsSummary.filter(student => {
            const isAssignedToFilter = student.assignedSections && student.assignedSections.some(section => section.id === selectedSectionFilter);
            return isAssignedToFilter;
        });


    const selectedProblem = allProblems.find(p => p.id === selectedProblemFilter);


    if (!isTeacher) {
        return (
            <Card className="bg-white/5 backdrop-blur-md border border-white/10 shadow-lg p-6 text-center">
                <CardTitle className="text-xl text-red-400 mb-2">Access Denied</CardTitle>
                <CardDescription className="text-gray-400">You do not have permission to view student progress.</CardDescription>
            </Card>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="animate-spin w-8 h-8 text-blue-400" />
                <span className="ml-3 text-lg text-gray-300">Loading student progress...</span>
            </div>
        );
    }

    if (error) {
        return (
            <Card className="bg-red-900/20 backdrop-blur-md border border-red-500/50 shadow-lg p-6 text-center">
                <CardTitle className="text-xl text-red-400 mb-2">Error</CardTitle>
                <CardDescription className="text-red-300">{error}</CardDescription>
            </Card>
        );
    }

    return (
        <Card className="bg-white/5 backdrop-blur-md border border-white/10 shadow-lg p-6">
            <CardHeader className="mb-4">
                {selectedStudent ? (
                    // Individual Student Detail View Header
                    <div className="flex items-center justify-between w-full">
                        <CardTitle className="text-2xl text-white">
                            Progress for: {selectedStudent.name || selectedStudent.email}
                        </CardTitle>
                        <Button
                            onClick={handleBackToSummary}
                            variant="ghost"
                            className="text-gray-300 hover:text-white ml-auto"
                        >
                            <ChevronRight className="w-5 h-5 ml-2 rotate-180" /> Back to All Students
                        </Button>
                    </div>
                ) : (
                    // Students Summary Dashboard Header
                    <div className="flex flex-col sm:flex-row justify-between items-center w-full">
                        <CardTitle className="text-2xl text-white mb-2 sm:mb-0">Student Progress Dashboard</CardTitle>
                        <div className="flex items-center gap-4 mt-2 sm:mt-0">
                            {/* Section Filter */}
                            <div className="flex items-center gap-2">
                                <label htmlFor="section-filter" className="text-gray-300 text-sm">Filter by Section:</label>
                                <select
                                    id="section-filter"
                                    value={selectedSectionFilter}
                                    onChange={(e) => setSelectedSectionFilter(e.target.value)}
                                    className="bg-gray-700 text-white border border-gray-600 rounded-md py-1 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="all">All Sections</option>
                                    {teacherSections.map(section => (
                                        <option key={section.id} value={section.id}>
                                            {section.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/* Problem Filter */}
                            <div className="flex items-center gap-2">
                                <label htmlFor="problem-filter" className="text-gray-300 text-sm">View Problem:</label>
                                <select
                                    id="problem-filter"
                                    value={selectedProblemFilter}
                                    onChange={(e) => setSelectedProblemFilter(e.target.value)}
                                    className="bg-gray-700 text-white border border-gray-600 rounded-md py-1 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="all">Overall Summary</option>
                                    {allProblems.map(problem => (
                                        <option key={problem.id} value={problem.id}>
                                            {problem.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}
                <CardDescription className="text-gray-400">
                    {selectedStudent ? "Detailed progress for each problem." : "Overview of all student progress."}
                </CardDescription>
            </CardHeader>
            <CardContent>
                {selectedStudent ? (
                    // Individual Student Detail View
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-700">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Problem</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Passed Tests</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Total Tests</th>
                                    <th scope="col" className="relative px-6 py-3">
                                        <span className="sr-only">View Code</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-gray-800 divide-y divide-gray-700">
                                {selectedStudentProblems.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-4 whitespace-nowrap text-center text-gray-400">No problems tracked for this student yet.</td>
                                    </tr>
                                ) : (
                                    selectedStudentProblems.map((problem) => (
                                        <tr key={problem.id} className="hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{problem.title}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {problem.progress.passedTests === problem.progress.totalTests && problem.progress.totalTests > 0 ? (
                                                    <span className="flex items-center text-green-400">
                                                        <CheckCircle className="w-4 h-4 mr-1" /> All Tests Passed
                                                    </span>
                                                ) : problem.progress.passedTests > 0 ? (
                                                    <span className="flex items-center text-orange-400">
                                                        <XCircle className="w-4 h-4 mr-1" /> Partial Success
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center text-red-400">
                                                        <XCircle className="w-4 h-4 mr-1" /> Not Attempted/Failed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                {problem.progress.passedTests}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                {problem.progress.totalTests}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                {/* Only show 'View Code' button if savedCode exists */}
                                                {problem.progress.savedCode ? (
                                                    <Button
                                                        onClick={() => handleViewCode(problem.title, problem.progress.savedCode)}
                                                        variant="outline"
                                                        className="text-blue-400 border-blue-400 hover:bg-blue-400/20"
                                                    >
                                                        View Code
                                                    </Button>
                                                ) : (
                                                    <span className="text-gray-500 italic">No Submission</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    // Students Summary Dashboard
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-700">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Student Email</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Section(s)</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Problems Solved</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Overall Test Pass Rate</th>
                                    {selectedProblemFilter !== 'all' && (
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                            {selectedProblem?.title || 'Problem Progress'}
                                        </th>
                                    )}
                                    <th scope="col" className="relative px-6 py-3">
                                        <span className="sr-only">View Details</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-gray-800 divide-y divide-gray-700">
                                {filteredStudents.length === 0 ? (
                                    <tr>
                                        <td colSpan={selectedProblemFilter !== 'all' ? "6" : "5"} className="px-6 py-4 whitespace-nowrap text-center text-gray-400">No students found for this filter or no progress recorded.</td>
                                    </tr>
                                ) : (
                                    filteredStudents.map((student) => (
                                        <tr key={student.uid} className="hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{student.email}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                {student.assignedSections.length > 0 ?
                                                    student.assignedSections.map(s => s.name).join(', ') :
                                                    'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                {student.problemsCompletedCount}/{student.totalAvailableProblems}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                {student.totalTestsOverall > 0 ? 
                                                    `${((student.passedTestsOverall / student.totalTestsOverall) * 100).toFixed(0)}% (${student.passedTestsOverall}/${student.totalTestsOverall})` : 
                                                    'N/A'}
                                            </td>
                                            {selectedProblemFilter !== 'all' && (
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                                    {(() => {
                                                        const problemProgress = student.detailedProgress[selectedProblemFilter];
                                                        if (problemProgress) {
                                                            const display = `${problemProgress.passedTests}/${problemProgress.totalTests} Passed`;
                                                            const colorClass = problemProgress.passedTests === problemProgress.totalTests && problemProgress.totalTests > 0
                                                                ? 'text-green-400'
                                                                : (problemProgress.passedTests > 0 ? 'text-orange-400' : 'text-red-400');
                                                            return <span className={colorClass}>{display}</span>;
                                                        }
                                                        const problemExists = allProblems.some(p => p.id === selectedProblemFilter);
                                                        return problemExists ? 'Not Attempted' : 'N/A (Problem not found)';
                                                    })()}
                                                </td>
                                            )}
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <Button 
                                                    onClick={() => handleViewDetails(student.uid)} 
                                                    variant="outline" 
                                                    className="text-blue-400 border-blue-400 hover:bg-blue-400/20"
                                                >
                                                    View Details
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>

            {/* Code Review Modal */}
            {showCodeReviewModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <Card className="w-full max-w-3xl bg-gray-900 border border-gray-700 shadow-lg p-6 rounded-lg relative">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleCloseCodeReviewModal}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white"
                        >
                            <XCircle className="h-6 w-6" />
                        </Button>
                        <CardHeader className="mb-4">
                            <CardTitle className="text-xl text-white">Code Submission for: {problemTitleForReview}</CardTitle>
                            <CardDescription className="text-gray-400">Review the student's submitted code.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Editor
                                value={codeToReview}
                                highlight={code => highlight(code, languages.java, 'java')}
                                padding={10}
                                className="min-h-[300px] bg-black/80 text-white border-gray-700 rounded-md shadow-inner
                                        font-mono text-sm overflow-auto"
                                style={{
                                    fontFamily: '"Fira code", "Fira Mono", monospace',
                                    fontSize: 14,
                                }}
                                readOnly={true} // Make the editor read-only
                            />
                        </CardContent>
                    </Card>
                </div>
            )}
        </Card>
    );
};

export default ProgressManager;
